import React, { useState } from 'react';

const ColumnBlock = ({ data, isSelected, onSelect, onUpdate, isPreview }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data);

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getColumnStyles = (columnCount) => {
    const baseStyles = {
      display: 'grid',
      gap: editData.gap || '1rem',
      padding: editData.padding || '1rem'
    };

    switch (columnCount) {
      case 1:
        return { ...baseStyles, gridTemplateColumns: '1fr' };
      case 2:
        return { ...baseStyles, gridTemplateColumns: '1fr 1fr' };
      case 3:
        return { ...baseStyles, gridTemplateColumns: '1fr 1fr 1fr' };
      default:
        return { ...baseStyles, gridTemplateColumns: '1fr' };
    }
  };

  if (isPreview) {
    return (
      <section style={{
        padding: '2rem 0',
        backgroundColor: editData.backgroundColor || 'transparent'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          {editData.title && (
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: editData.titleColor || '#333',
              textAlign: editData.titleAlign || 'left'
            }}>
              {editData.title}
            </h2>
          )}
          
          <div style={getColumnStyles(editData.columnCount || 1)}>
            {Array.from({ length: editData.columnCount || 1 }, (_, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: editData.columnBackground || 'white',
                  padding: editData.columnPadding || '1.5rem',
                  borderRadius: editData.columnBorderRadius || '0.5rem',
                  boxShadow: editData.columnShadow || '0 2px 4px rgba(0,0,0,0.1)',
                  border: editData.columnBorder || '1px solid #e5e7eb'
                }}
              >
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  color: editData.columnTextColor || '#333'
                }}>
                  Column {index + 1}
                </h3>
                <p style={{
                  color: editData.columnTextColor || '#666',
                  lineHeight: '1.6'
                }}>
                  {editData[`column${index + 1}Content`] || 'Add your content here...'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <div
      style={{
        border: isSelected ? '2px solid #007bff' : '2px solid #dee2e6',
        borderRadius: '0.5rem',
        backgroundColor: 'white',
        marginBottom: '1rem',
        position: 'relative'
      }}
      onClick={onSelect}
    >
      {/* Block Header */}
      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📊</span>
          <span style={{ fontWeight: '600' }}>Column Layout ({editData.columnCount || 1} columns)</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(!isEditing);
          }}
          style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: isEditing ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Block Content */}
      <div style={{ padding: '1rem' }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Column Count */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Number of Columns
              </label>
              <select
                value={editData.columnCount || 1}
                onChange={(e) => handleInputChange('columnCount', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value={1}>1 Column</option>
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Section Title (Optional)
              </label>
              <input
                type="text"
                value={editData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter section title"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Title Alignment */}
            {editData.title && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Title Alignment
                </label>
                <select
                  value={editData.titleAlign || 'left'}
                  onChange={(e) => handleInputChange('titleAlign', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.25rem',
                    fontSize: '1rem'
                  }}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            )}

            {/* Column Content */}
            {Array.from({ length: editData.columnCount || 1 }, (_, index) => (
              <div key={index}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Column {index + 1} Content
                </label>
                <textarea
                  value={editData[`column${index + 1}Content`] || ''}
                  onChange={(e) => handleInputChange(`column${index + 1}Content`, e.target.value)}
                  placeholder={`Enter content for column ${index + 1}`}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>
            ))}

            {/* Spacing */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Gap Between Columns
              </label>
              <select
                value={editData.gap || '1rem'}
                onChange={(e) => handleInputChange('gap', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="0.5rem">Small (0.5rem)</option>
                <option value="1rem">Medium (1rem)</option>
                <option value="1.5rem">Large (1.5rem)</option>
                <option value="2rem">Extra Large (2rem)</option>
              </select>
            </div>

            {/* Background Color */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Section Background Color
              </label>
              <input
                type="color"
                value={editData.backgroundColor || '#ffffff'}
                onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.25rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  height: '40px'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.25rem',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
              {editData.title || 'Column Layout'}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
              {editData.columnCount || 1} column(s) • Click edit to customize
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnBlock;
